import imageData from '../data/images.json';
import { useTranslation } from 'react-i18next';

/**
 * ÇelikSac Görsel Yükleyici
 * 
 * Bu modül, images.json dosyasından görsel URL'lerini yüklemeyi kolaylaştırır.
 * Cloudinary linklerini merkezi bir şekilde yönetmek için ve i18n desteğiyle
 * çoklu dil için kullanılır.
 */

/**
 * Ürün görseli URL'sini döndürür
 * @param {string} categoryId - Ürün kategorisi ID'si (örn. "harrowDiscs")
 * @param {string} productId - Ürün ID'si (örn. "harrow-a")
 * @param {boolean} thumbnail - Küçük resim mi yoksa tam boy mu
 * @returns {string} Görsel URL'si
 */
export const getProductImage = (categoryId, productId, thumbnail = false) => {
  try {
    const category = imageData.products[categoryId];
    if (!category) return null;
    
    const product = category.find(item => item.id === productId);
    if (!product) return null;
    
    return thumbnail ? product.thumbnail : product.url;
  } catch (error) {
    console.error(`Error loading image for product ${productId}:`, error);
    return null;
  }
};

/**
 * Ürün bilgilerini döndürür (URL ve çeviri anahtarları dahil)
 * @param {string} categoryId - Ürün kategorisi ID'si
 * @param {string} productId - Ürün ID'si
 * @returns {Object} Ürün bilgileri
 */
export const getProductData = (categoryId, productId) => {
  try {
    const category = imageData.products[categoryId];
    if (!category) return null;
    
    const product = category.find(item => item.id === productId);
    if (!product) return null;
    
    return product;
  } catch (error) {
    console.error(`Error loading data for product ${productId}:`, error);
    return null;
  }
};

/**
 * Sertifika görseli URL'sini döndürür
 * @param {string} certificateId - Sertifika ID'si (örn. "iso9001")
 * @param {boolean} thumbnail - Küçük resim mi yoksa tam boy mu
 * @param {boolean} pdf - PDF dosyası mı yoksa görsel mi
 * @returns {string} Görsel veya PDF URL'si
 */
export const getCertificateImage = (certificateId, thumbnail = false, pdf = false) => {
  try {
    const certificate = imageData.certificates[certificateId];
    if (!certificate) return null;
    
    if (pdf) return certificate.pdfUrl;
    return thumbnail ? certificate.thumbnail : certificate.url;
  } catch (error) {
    console.error(`Error loading image for certificate ${certificateId}:`, error);
    return null;
  }
};

/**
 * Sertifika bilgilerini döndürür (URL ve çeviri anahtarları dahil)
 * @param {string} certificateId - Sertifika ID'si
 * @returns {Object} Sertifika bilgileri
 */
export const getCertificateData = (certificateId) => {
  try {
    const certificate = imageData.certificates[certificateId];
    return certificate || null;
  } catch (error) {
    console.error(`Error loading data for certificate ${certificateId}:`, error);
    return null;
  }
};

/**
 * Hero görseli URL'sini döndürür
 * @param {string} page - Sayfa adı (örn. "home", "about")
 * @param {number} index - Eğer görseller bir dizi ise, belirli bir indeksteki görseli döndürür
 * @returns {string} Görsel URL'si
 */
export const getHeroImage = (page, index = 0) => {
  try {
    const heroData = imageData.hero[page];
    
    // Eğer dizi ise belirli bir indeksteki görseli, değilse doğrudan URL'yi döndür
    if (Array.isArray(heroData)) {
      return heroData[index] || heroData[0] || null;
    }
    
    return heroData || null;
  } catch (error) {
    console.error(`Error loading hero image for ${page}:`, error);
    return null;
  }
};

/**
 * About sayfası fabrika görselini döndürür
 * @returns {string} Görsel URL'si
 */
export const getAboutFactoryImage = () => {
  try {
    return imageData.about?.factory || null;
  } catch (error) {
    console.error('Error loading factory image:', error);
    return null;
  }
};

/**
 * About sayfası üretim görselini döndürür
 * @returns {string} Görsel URL'si
 */
export const getAboutProductionImage = () => {
  try {
    return imageData.about?.production || null;
  } catch (error) {
    console.error('Error loading production image:', error);
    return null;
  }
};

/**
 * Bir sayfaya ait tüm hero görsellerinin URL'lerini dizi olarak döndürür
 * @param {string} page - Sayfa adı (örn. "home", "about")
 * @returns {array} Görsel URL'leri dizisi veya tekil görsel içeren dizi
 */
export const getHeroImages = (page) => {
  try {
    const heroData = imageData.hero[page];
    
    if (Array.isArray(heroData)) {
      return heroData;
    }
    
    // Tekil görsel varsa, onu da bir dizi olarak döndür
    return heroData ? [heroData] : [];
    
  } catch (error) {
    console.error(`Error loading hero images for ${page}:`, error);
    return [];
  }
};

/**
 * Anasayfa ürün görsellerinin URL'sini döndürür
 * @param {string} productKey - Ürün anahtarı (örn. "product1", "product2")
 * @returns {string} Görsel URL'si
 */
export const getHomeProductImage = (productKey) => {
  try {
    return imageData.home.products[productKey] || null;
  } catch (error) {
    console.error(`Error loading home product image (${productKey}):`, error);
    return null;
  }
};

/**
 * Logo URL'sini döndürür
 * @param {string} variant - Logo çeşidi (örn. "primary", "white", "dark", "favicon")
 * @returns {string} Logo URL'si
 */
export const getLogo = (variant = 'primary') => {
  try {
    return imageData.logo[variant] || imageData.logo.primary;
  } catch (error) {
    console.error(`Error loading logo (${variant}):`, error);
    return null;
  }
};

/**
 * Alt metin çeviri anahtarlarını döndürür
 * @param {string} type - Alt metin tipi ("logo", "productDefault", "certificateDefault")
 * @returns {string} Çeviri anahtarı
 */
export const getAltTextKey = (type) => {
  try {
    return imageData.altTextKeys[type] || null;
  } catch (error) {
    console.error(`Error loading alt text key for ${type}:`, error);
    return null;
  }
};

// React bileşenleri için Hook kullanarak tüm ürünlere erişim
export const useProductImageWithTranslation = (categoryId, productId, thumbnail = false) => {
  const { t } = useTranslation();
  const imageUrl = getProductImage(categoryId, productId, thumbnail);
  const productData = getProductData(categoryId, productId);
  
  if (!productData) return { imageUrl, title: '', description: '', alt: '' };
  
  const title = productData.titleKey ? t(productData.titleKey) : '';
  const description = productData.descriptionKey ? t(productData.descriptionKey) : '';
  const alt = t(getAltTextKey('productDefault'), { title });
  
  return { imageUrl, title, description, alt };
};

// React bileşenleri için Hook kullanarak tüm sertifika bilgilerine erişim
export const useCertificateImageWithTranslation = (certificateId, thumbnail = false, pdf = false) => {
  const { t } = useTranslation();
  const imageUrl = getCertificateImage(certificateId, thumbnail, pdf);
  const certificateData = getCertificateData(certificateId);
  
  if (!certificateData) return { imageUrl, title: '', description: '', alt: '' };
  
  const title = certificateData.titleKey ? t(certificateData.titleKey) : '';
  const description = certificateData.descriptionKey ? t(certificateData.descriptionKey) : '';
  const alt = t(getAltTextKey('certificateDefault'), { title });
  
  return { imageUrl, title, description, alt };
};

export default {
  getProductImage,
  getProductData,
  getCertificateImage,
  getCertificateData,
  getHeroImage,
  getHeroImages,
  getAboutFactoryImage,
  getAboutProductionImage,
  getHomeProductImage,
  getLogo,
  getAltTextKey,
  useProductImageWithTranslation,
  useCertificateImageWithTranslation
};
